import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2Icon, ClockIcon, LifeBuoyIcon, MailIcon, ShieldAlertIcon } from 'lucide-react';
import { Card } from '../../components/ui/Panels';
import { Button, ButtonLink } from '../../components/ui/Button';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { spring } from '../../utils/motion';
type Status = 'idle' | 'submitting' | 'sent' | 'error';
interface Errors {
  name?: string;
  email?: string;
  message?: string;
}
export function Contact() {
  const reduce = useReducedMotion();
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Errors>({});
  const [form, setForm] = useState({
    name: '',
    email: '',
    topic: 'booking',
    message: ''
  });
  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(prev => ({
    ...prev,
    [key]: e.target.value
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!form.name.trim()) next.name = 'Tell us who you are.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'That email does not look right.';
    if (form.message.trim().length < 12) next.message = 'A little more detail helps us route this.';
    setErrors(next);
    if (Object.keys(next).length) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('sent'), 700);
  };
  return <div className="mx-auto grid max-w-shell gap-10 px-5 py-16 lg:grid-cols-[1fr_0.8fr] lg:px-8 lg:py-20" data-id="element-2192" data-mp="3jx8joz9i5vzt">
      <div data-id="element-2193" data-mp="3ny2b42m5lsxg">
        <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-action" data-id="element-2194" data-mp="3nzlrdt7t83z8">Contact</p>
        <h1 className="mt-4 max-w-[24ch] font-display text-[clamp(2rem,3.8vw,3.2rem)] font-semibold leading-[1.05] tracking-[-0.03em] text-ink" data-id="element-2195" data-mp="39zu5l6b1xjao">
          Talk to a person who knows the kitchen.
        </h1>
        <p className="mt-4 max-w-[54ch] text-lg leading-relaxed text-ink-2" data-id="element-2196" data-mp="1kigu8tuodhif">
          For anything tied to a live booking, a support ticket is faster — it arrives with the booking, the quote, and the
          allergy record attached.
        </p>

        <AnimatePresence mode="wait" data-id="element-2197" data-mp="16q9079us04nf">
          {status === 'sent' ? <motion.div key="sent" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 12,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={reduce ? {
          duration: 0.15
        } : spring.smooth} className="mt-8 rounded-2xl border border-hairline bg-success-soft p-6" data-id="element-2198" data-mp="16s3lip9ylys1">
              <CheckCircle2Icon className="h-6 w-6 text-success" aria-hidden="true" data-id="element-2199" data-mp="1phjr3r0i4hfb" />
              <h2 className="mt-3 font-display text-xl font-semibold text-ink" data-id="element-2200" data-mp="j17nhaewu7cc">Message received</h2>
              <p className="mt-2 max-w-[52ch] text-sm text-ink-2" data-id="element-2201" data-mp="e121oavbnf26">
                We reply to everything within one working day, and inside two hours for anything marked safety. A copy is on its
                way to {form.email}.
              </p>
              <Button variant="outline" size="sm" className="mt-5" onClick={() => setStatus('idle')} data-id="element-2202" data-mp="8x12ahxf7lkk">
                Send another
              </Button>
            </motion.div> : <motion.form key="form" onSubmit={submit} noValidate initial={false} className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-2203" data-mp="invlteja872b">
              <Field label="Your name" id="c-name" required error={errors.name} data-id="element-2204" data-mp="rpby5rtnqj96">
                <Input id="c-name" value={form.name} onChange={update('name')} autoComplete="name" data-id="element-2205" data-mp="wmva2l2vujxy" />
              </Field>
              <Field label="Email" id="c-email" required error={errors.email} data-id="element-2206" data-mp="11jc764s288n6">
                <Input id="c-email" type="email" value={form.email} onChange={update('email')} autoComplete="email" data-id="element-2207" data-mp="2yfjhl6mk7tjj" />
              </Field>
              <Field label="What is this about?" id="c-topic" className="sm:col-span-2" data-id="element-2208" data-mp="xo97ydhljxyu">
                <Select id="c-topic" value={form.topic} onChange={update('topic')} data-id="element-2209" data-mp="spervjb0knif">
                  <option value="booking" data-id="element-2210" data-mp="srisnsdm96eh">A booking or quote</option>
                  <option value="chef" data-id="element-2211" data-mp="16nwdnbhczpxp">Joining as a chef</option>
                  <option value="safety" data-id="element-2212" data-mp="11pl28wbf17dd">Food safety or an allergen</option>
                  <option value="press" data-id="element-2213" data-mp="e0i9otf8tu43">Press or partnerships</option>
                  <option value="other" data-id="element-2214" data-mp="e0s1mmjumbcf">Something else</option>
                </Select>
              </Field>
              <Field label="Message" id="c-message" required error={errors.message} hint="Include a booking reference if you have one" className="sm:col-span-2" data-id="element-2215" data-mp="9wu1c8iseuhs">
                <Textarea id="c-message" rows={6} value={form.message} onChange={update('message')} data-id="element-2216" data-mp="3re3smw7uia3l" />
              </Field>
              <div className="flex flex-wrap items-center gap-3 sm:col-span-2" data-id="element-2217" data-mp="3mg21rp46h6hk">
                <Button type="submit" variant="action" size="lg" disabled={status === 'submitting'} data-id="element-2218" data-mp="21wq57le72huz">
                  {status === 'submitting' ? 'Sending…' : 'Send message'}
                </Button>
                <p className="text-[13px] text-ink-3" data-id="element-2219" data-mp="1sz6184oy67go">We never share your details with chefs until you book.</p>
              </div>
              {status === 'error' && <p role="alert" className="text-sm font-medium text-danger sm:col-span-2" data-id="element-2220" data-mp="2lm0lb0hur4tm">
                  Three fields need a look before this can send.
                </p>}
            </motion.form>}
        </AnimatePresence>
      </div>

      <div className="space-y-4" data-id="element-2221" data-mp="2gn7530b6kzpr">
        <Card className="p-5" data-id="element-2222" data-mp="2gme64ligrei6">
          <ClockIcon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-2223" data-mp="2glmcvhcc9sv2" />
          <h2 className="mt-3 font-display text-lg font-semibold text-ink" data-id="element-2224" data-mp="107w3bv2gace1">Response times</h2>
          <dl className="mt-3 space-y-2 text-sm" data-id="element-2225" data-mp="1591ys0nhwdr9">
            <div className="flex justify-between gap-4" data-id="element-2226" data-mp="155nw0rax62ja">
              <dt className="text-ink-2" data-id="element-2227" data-mp="l4mb5clf7abn">Safety &amp; allergens</dt>
              <dd className="font-medium text-ink" data-id="element-2228" data-mp="h3smui42fti8">Under 2 hrs</dd>
            </div>
            <div className="flex justify-between gap-4" data-id="element-2229" data-mp="cdlzellaif1k">
              <dt className="text-ink-2" data-id="element-2230" data-mp="cc17z4u9vc7x">Live bookings</dt>
              <dd className="font-medium text-ink" data-id="element-2231" data-mp="7f11istig7ga">Same day</dd>
            </div>
            <div className="flex justify-between gap-4" data-id="element-2232" data-mp="2fenih5uyn6f">
              <dt className="text-ink-2" data-id="element-2233" data-mp="2gzfq04p9u14">Everything else</dt>
              <dd className="font-medium text-ink" data-id="element-2234" data-mp="3tl7o14h6ank1">1 working day</dd>
            </div>
          </dl>
        </Card>

        <Card className="p-5" data-id="element-2235" data-mp="3pl5l6rzpbo36">
          <LifeBuoyIcon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-2236" data-mp="3pkdzq9ztj6qm" />
          <h2 className="mt-3 font-display text-lg font-semibold text-ink" data-id="element-2237" data-mp="3kmcg9nyhd5em">Already booked?</h2>
          <p className="mt-2 text-sm text-ink-2" data-id="element-2238" data-mp="1nrg2m1z0drzv">
            Open a ticket from inside your account and it arrives with the booking attached.
          </p>
          <ButtonLink to="/app/support" variant="outline" size="sm" className="mt-4" data-id="element-2239" data-mp="1qd73u71bnfhz">
            Open a support ticket
          </ButtonLink>
        </Card>

        <Card className="p-5" data-id="element-2240" data-mp="1vap8vmc7xakp">
          <ShieldAlertIcon className="h-5 w-5 text-action" aria-hidden="true" data-id="element-2241" data-mp="2030bwy299chh" />
          <h2 className="mt-3 font-display text-lg font-semibold text-ink" data-id="element-2242" data-mp="2053aw7ldyfyc">Urgent during a booking</h2>
          <p className="mt-2 text-sm text-ink-2" data-id="element-2243" data-mp="25463gsk6itpf">
            If someone is having a reaction, call emergency services first. Then call our on-call line — it rings a human,
            not a queue.
          </p>
          <p className="mt-3 flex items-center gap-2 text-sm font-medium text-ink" data-id="element-2244" data-mp="1cf8x9nt8snd1">
            <MailIcon className="h-4 w-4 text-ink-3" aria-hidden="true" data-id="element-2245" data-mp="1helnmijbockd" /> safety@mise.example
          </p>
        </Card>
      </div>
    </div>;
}